import { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import type { UserProfileDto } from '../../../domain/contracts/api.contracts';
import { useCountdownNow } from '../../../application/hooks/useCountdownNow';
import {
  useAvatarCooldown,
  useAvatarImage,
} from '../../../application/hooks/useProfileAvatar';
import { splitCountdown } from '../../../domain/avatar/prepareAvatar';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { AvatarChangeDialog } from './AvatarChangeDialog';

export interface ProfileAvatarProps {
  user: Pick<UserProfileDto, 'id' | 'fullName' | 'hasAvatar' | 'avatarUpdatedAt' | 'avatarNextChangeAt'>;
  initials: string;
}

export function ProfileAvatar({ user, initials }: ProfileAvatarProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  const [now] = useCountdownNow();
  const cooldown = useAvatarCooldown(user, now);
  const { url } = useAvatarImage(user);

  // If avatar version changes, retry loading
  useEffect(() => {
    setImgFailed(false);
  }, [user?.avatarUpdatedAt]);

  const showImage = Boolean(user?.hasAvatar) && Boolean(url) && !imgFailed;

  const parts = splitCountdown(cooldown.remainingMs);
  const countdownStr =
    parts.days > 0
      ? `${parts.days}d ${parts.hours}h ${parts.minutes}m ${parts.seconds}s`
      : parts.hours > 0
      ? `${parts.hours}h ${parts.minutes}m ${parts.seconds}s`
      : parts.minutes > 0
      ? `${parts.minutes}m ${parts.seconds}s`
      : `${parts.seconds}s`;

  const nextDateStr = cooldown.nextChangeAt ? new Date(cooldown.nextChangeAt).toLocaleString() : '';

  return (
    <div className="profile-avatar-wrapper" data-testid="profile-avatar-wrapper">
      <div className="profile-avatar-circle" data-testid="profile-avatar-circle">
        {showImage ? (
          <img
            src={url!}
            alt={user.fullName}
            className="profile-avatar-img"
            onError={() => setImgFailed(true)}
            data-testid="profile-avatar-image"
          />
        ) : (
          <span className="profile-avatar-initials" data-testid="profile-avatar-initials">
            {initials}
          </span>
        )}

        <button
          type="button"
          className="profile-avatar-overlay-btn"
          onClick={() => setDialogOpen(true)}
          disabled={cooldown.active}
          aria-label={
            cooldown.active
              ? `${t('profile.avatar.changePhoto')} (${countdownStr})`
              : t('profile.avatar.changePhoto')
          }
          title={
            cooldown.active
              ? `${t('profile.avatar.changePhotoDisabled', { date: nextDateStr })} — ${countdownStr}`
              : t('profile.avatar.changePhoto')
          }
          data-testid="change-photo-btn"
        >
          <Camera size={16} className="profile-avatar-camera-icon" />
        </button>
      </div>

      {cooldown.active && (
        <div className="profile-avatar-cooldown" data-testid="profile-avatar-cooldown">
          <span className="avatar-countdown-text" data-testid="avatar-countdown-text">
            {countdownStr}
          </span>
        </div>
      )}

      {dialogOpen && (
        <AvatarChangeDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCooldownServer={cooldown.applyServer}
        />
      )}
    </div>
  );
}
